/**
 * CcpaDsrPage — CCPA Data Subject Request console.
 *
 * Privacy-operator workbench for CCPA (California) subject requests:
 * - Submit a new request on behalf of a subject (know / delete /
 * opt_out_sale / limit_sensitive).
 * - Triage open requests pulled from GET /api/v1/ccpa-dsr/requests
 * with derived `days_since_received` and `sla_breached` flags.
 * - Transition a request to fulfilled or denied (with reason).
 *
 * Visual language: brand green only (green-600 / green-700 / green-50),
 * white + zinc neutrals, Inter, 6-8px radii, inline SVG icons.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCcpaDsrList,
	useCcpaDsrCreate,
	useCcpaDsrPatchStatus,
} from "./hooks";
import {
	CCPA_REQUEST_TYPES,
	CCPA_TYPE_LABELS,
	type CcpaRequestRow,
	type CcpaRequestStatus,
	type CcpaRequestType,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — brand green, zinc borders, 6/8 rounding.
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:border-green-700 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

const STATUS_PILL: Record<CcpaRequestStatus, string> = {
	received: "border-zinc-200 bg-zinc-50 text-zinc-700",
	verified: "border-green-200 bg-green-50 text-green-700",
	fulfilled: "border-green-300 bg-green-100 text-green-800",
	denied: "border-red-200 bg-red-50 text-red-700",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface CcpaDsrPageProps {
	/** Injected clock for tests — defaults to Date.now. */
	now?: () => number;
}

export function CcpaDsrPage(props: CcpaDsrPageProps = {}) {
	const nowFn = props.now ?? (() => Date.now());
	return (
 <main
 data-testid="ccpa-dsr-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16"
 >
 <Header />
 <SubmitCard />
 <RequestsCard nowFn={nowFn} />
 <OperatorChecklist />
 </main>
	);
}

export default CcpaDsrPage;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
	return (
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
 Privacy / California
 </p>
 <h1 className="text-3xl font-semibold tracking-tight text-green-700 sm:text-4xl">
 CCPA subject request console
 </h1>
 <p className="max-w-prose text-sm text-zinc-600">
 File, verify, and close out California Consumer Privacy Act data
 subject requests. Each request is tenant-scoped, timed against the
 45-day statutory SLA, and closed only by explicit operator action.
 </p>
 </header>
	);
}

// ---------------------------------------------------------------------------
// Submit card
// ---------------------------------------------------------------------------

interface FormState {
	subject_email: string;
	ca_resident_attested: boolean;
	type: CcpaRequestType;
}

const EMPTY_FORM: FormState = {
	subject_email: "",
	ca_resident_attested: false,
	type: "know",
};

function SubmitCard() {
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [formError, setFormError] = useState<string | null>(null);
	const createMut = useCcpaDsrCreate();

	const handleSubmit = (e: FormEvent) => {
 e.preventDefault();
 setFormError(null);
 const trimmed = form.subject_email.trim();
 if (!trimmed.includes("@")) {
 setFormError("Please enter a valid subject email.");
 return;
 }
 if (!form.ca_resident_attested) {
 setFormError("CA residency attestation is required.");
 return;
 }
 createMut.mutate(
 {
 subject_email: trimmed,
 ca_resident_attested: form.ca_resident_attested,
 type: form.type,
 },
 {
 onSuccess: () => setForm(EMPTY_FORM),
 },
 );
	};

	return (
 <section
 className={`${CARD} flex flex-col gap-5`}
 aria-labelledby="ccpa-dsr-submit-heading"
 >
 <div className="flex flex-col gap-1">
 <h2
 id="ccpa-dsr-submit-heading"
 className="text-lg font-semibold text-zinc-900"
 >
 File a new request
 </h2>
 <p className={HINT}>
 POSTs to /api/v1/ccpa-dsr/requests. The subject receives a one-time
 verification token on creation.
 </p>
 </div>

 <form
 onSubmit={handleSubmit}
 className="flex flex-col gap-5"
 data-testid="ccpa-dsr-create-form"
 noValidate
 >
 <div className="flex flex-col gap-1.5">
 <label htmlFor="ccpa-subject-email" className={LABEL}>
 Subject email
 </label>
 <input
 id="ccpa-subject-email"
 type="email"
 autoComplete="off"
 placeholder="subject@example.com"
 className={INPUT}
 value={form.subject_email}
 onChange={(e) =>
 setForm((p) => ({ ...p, subject_email: e.currentTarget.value }))
 }
 disabled={createMut.isPending}
 data-testid="ccpa-dsr-email-input"
 />
 <p className={HINT}>
 The verification token returned on creation is delivered to this
 address out-of-band by the operator.
 </p>
 </div>

 <fieldset className="flex flex-col gap-2">
 <legend className={LABEL}>Request type</legend>
 <div
 role="radiogroup"
 aria-label="Request type"
 className="flex flex-wrap gap-2"
 >
 {CCPA_REQUEST_TYPES.map((t) => {
 const active = form.type === t;
 return (
 <button
 key={t}
 type="button"
 role="radio"
 aria-checked={active}
 onClick={() => setForm((p) => ({ ...p, type: t }))}
 disabled={createMut.isPending}
 data-type={t}
 className={[
 "inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition-colors",
 active
 ? "border-green-600 bg-green-50 text-green-700"
 : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300",
 ].join("")}
 >
 <span
 aria-hidden="true"
 className={[
 "h-1.5 w-1.5 rounded-full",
 active ? "bg-green-600" : "bg-zinc-300",
 ].join("")}
 />
 {CCPA_TYPE_LABELS[t]}
 </button>
 );
 })}
 </div>
 </fieldset>

 <label className="flex items-start gap-2 text-sm text-zinc-700">
 <input
 type="checkbox"
 className="mt-0.5 h-4 w-4 rounded-[4px] border-zinc-300 text-green-600 focus:ring-green-600"
 checked={form.ca_resident_attested}
 onChange={(e) =>
 setForm((p) => ({
 ...p,
 ca_resident_attested: e.currentTarget.checked,
 }))
 }
 disabled={createMut.isPending}
 data-testid="ccpa-dsr-ca-resident"
 />
 <span>
 The subject attests California residency. Required to proceed under
 1798.100(a).
 </span>
 </label>

 {formError ? (
 <p className={ERROR_TEXT} role="alert">
 {formError}
 </p>
 ) : null}

 {createMut.error ? (
 <p className={ERROR_TEXT} role="alert" data-testid="ccpa-dsr-create-error">
 {createMut.error.message}
 </p>
 ) : null}

 {createMut.isSuccess && createMut.data ? (
 <div
 className="rounded-[6px] border border-green-200 bg-green-50 p-3 text-sm text-green-800"
 data-testid="ccpa-dsr-create-success"
 >
 <p className="font-medium">Request recorded.</p>
 <p className="mt-1 text-xs text-green-700">
 Verification token (one-time, deliver to subject):
 </p>
 <p className="mt-1 break-all font-mono text-xs text-green-900">
 {createMut.data.verification_token}
 </p>
 </div>
 ) : null}

 <div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => {
 setForm(EMPTY_FORM);
 setFormError(null);
 }}
 disabled={createMut.isPending}
 >
 Reset
 </button>
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={createMut.isPending}
 data-testid="ccpa-dsr-submit"
 >
 {createMut.isPending ? "Submitting." : "File request"}
 </button>
 </div>
 </form>
 </section>
	);
}

// ---------------------------------------------------------------------------
// Requests card
// ---------------------------------------------------------------------------

const STATUS_FILTERS: Array<{ value: "" | CcpaRequestStatus; label: string }> =
	[
 { value: "", label: "All" },
 { value: "received", label: "Received" },
 { value: "verified", label: "Verified" },
 { value: "fulfilled", label: "Fulfilled" },
 { value: "denied", label: "Denied" },
	];

function RequestsCard(props: { nowFn: () => number }) {
	const [filter, setFilter] = useState<"" | CcpaRequestStatus>("");
	const listQuery = useCcpaDsrList(filter === "" ? undefined : filter);

	return (
 <section
 className={`${CARD} flex flex-col gap-4`}
 aria-labelledby="ccpa-dsr-list-heading"
 >
 <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <div className="flex flex-col gap-1">
 <h2
 id="ccpa-dsr-list-heading"
 className="text-lg font-semibold text-zinc-900"
 >
 Tenant request log
 </h2>
 <p className={HINT}>
 Live from GET /api/v1/ccpa-dsr/requests. Derived days_since_received
 and sla_breached are computed server-side.
 </p>
 </div>
 <div
 role="tablist"
 aria-label="Status filter"
 className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
 >
 {STATUS_FILTERS.map((opt) => {
 const active = filter === opt.value;
 return (
 <button
 key={opt.value || "all"}
 type="button"
 role="tab"
 aria-selected={active}
 onClick={() => setFilter(opt.value)}
 data-status-filter={opt.value || "all"}
 className={[
 "rounded-[6px] px-3 py-1 text-xs font-medium transition-colors",
 active
 ? "bg-white text-zinc-900 shadow-sm"
 : "text-zinc-600 hover:text-zinc-900",
 ].join("")}
 >
 {opt.label}
 </button>
 );
 })}
 </div>
 </div>

 {listQuery.isLoading ? (
 <p className={HINT} data-testid="ccpa-dsr-loading">
 Loading.
 </p>
 ) : listQuery.error ? (
 <p className={ERROR_TEXT} role="alert">
 {listQuery.error.message}
 </p>
 ) : (listQuery.data ?? []).length === 0 ? (
 <p className={HINT} data-testid="ccpa-dsr-empty">
 No requests match the current filter.
 </p>
 ) : (
 <RequestsTable
 rows={listQuery.data!}
 nowMs={props.nowFn()}
 />
 )}
 </section>
	);
}

function RequestsTable(props: { rows: CcpaRequestRow[]; nowMs: number }) {
	return (
 <div className="overflow-x-auto" data-testid="ccpa-dsr-list">
 <table className="w-full border-collapse text-left text-sm">
 <thead>
 <tr className="border-b border-zinc-200 text-xs font-medium uppercase tracking-wide text-zinc-500">
 <th className="py-2 pr-3">ID</th>
 <th className="py-2 pr-3">Email</th>
 <th className="py-2 pr-3">Type</th>
 <th className="py-2 pr-3">Status</th>
 <th className="py-2 pr-3">Day / 45</th>
 <th className="py-2 pr-3 text-right">Action</th>
 </tr>
 </thead>
 <tbody>
 {props.rows.map((row) => (
 <RequestsRow key={row.id} row={row} nowMs={props.nowMs} />
 ))}
 </tbody>
 </table>
 </div>
	);
}

function RequestsRow(props: { row: CcpaRequestRow; nowMs: number }) {
	const patchMut = useCcpaDsrPatchStatus();
	const [denyMode, setDenyMode] = useState(false);
	const [denyReason, setDenyReason] = useState("");

	const canClose =
 props.row.status === "received" || props.row.status === "verified";

	const submitted = useMemo(
 () => new Date(props.row.received_at).toISOString().slice(0, 10),
 [props.row.received_at],
	);

	const slaPill = props.row.sla_breached
 ? "border-red-200 bg-red-50 text-red-700"
 : props.row.days_since_received >= 35
 ? "border-green-200 bg-green-50 text-green-700"
 : "border-green-200 bg-green-50 text-green-700";

	return (
 <tr
 className="border-b border-zinc-100 last:border-b-0"
 data-testid={`ccpa-dsr-row-${props.row.id}`}
 data-row-status={props.row.status}
 >
 <td className="py-3 pr-3 font-mono text-xs text-zinc-600">
 {props.row.id.slice(0, 12)}
 </td>
 <td className="py-3 pr-3 text-zinc-900">{props.row.subject_email}</td>
 <td className="py-3 pr-3 text-zinc-700">
 <span className="inline-flex items-center gap-1.5">
 <TypeIcon type={props.row.type} />
 {CCPA_TYPE_LABELS[props.row.type]}
 </span>
 <p className="mt-0.5 text-xs text-zinc-500">submitted {submitted}</p>
 </td>
 <td className="py-3 pr-3">
 <span
 className={[
 "inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium capitalize",
 STATUS_PILL[props.row.status],
 ].join("")}
 >
 {props.row.status}
 </span>
 </td>
 <td className="py-3 pr-3">
 <span
 className={[
 "inline-flex items-center gap-1.5 rounded-[6px] border px-2 py-0.5 text-xs font-medium",
 slaPill,
 ].join("")}
 data-sla-breached={props.row.sla_breached ? "true" : "false"}
 >
 <span
 aria-hidden="true"
 className={[
 "h-1.5 w-1.5 rounded-full",
 props.row.sla_breached
 ? "bg-red-500"
 : props.row.days_since_received >= 35
 ? "bg-green-500"
 : "bg-green-600",
 ].join("")}
 />
 {props.row.days_since_received} / 45
 </span>
 </td>
 <td className="py-3 pr-3 text-right">
 {!canClose ? (
 <span className={HINT}>closed</span>
 ) : denyMode ? (
 <div className="flex flex-col items-end gap-2">
 <input
 type="text"
 className={`${INPUT} w-56`}
 placeholder="Denial reason (required)"
 value={denyReason}
 onChange={(e) => setDenyReason(e.currentTarget.value)}
 disabled={patchMut.isPending}
 data-testid={`ccpa-dsr-deny-reason-${props.row.id}`}
 />
 <div className="flex items-center gap-2">
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => {
 setDenyMode(false);
 setDenyReason("");
 }}
 disabled={patchMut.isPending}
 >
 Cancel
 </button>
 <button
 type="button"
 className={BTN_DANGER}
 onClick={() => {
 if (!denyReason.trim()) return;
 patchMut.mutate({
 id: props.row.id,
 body: {
 status: "denied",
 denial_reason: denyReason.trim(),
 },
 });
 }}
 disabled={patchMut.isPending || !denyReason.trim()}
 data-testid={`ccpa-dsr-confirm-deny-${props.row.id}`}
 >
 {patchMut.isPending ? "Saving." : "Confirm deny"}
 </button>
 </div>
 </div>
 ) : (
 <div className="flex items-center justify-end gap-2">
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => setDenyMode(true)}
 disabled={patchMut.isPending}
 data-testid={`ccpa-dsr-deny-${props.row.id}`}
 >
 Deny
 </button>
 <button
 type="button"
 className={BTN_PRIMARY}
 onClick={() =>
 patchMut.mutate({
 id: props.row.id,
 body: { status: "fulfilled" },
 })
 }
 disabled={patchMut.isPending}
 data-testid={`ccpa-dsr-fulfill-${props.row.id}`}
 >
 {patchMut.isPending ? "Saving." : "Fulfill"}
 </button>
 </div>
 )}
 {patchMut.error ? (
 <p className="mt-1 text-xs text-red-600">{patchMut.error.message}</p>
 ) : null}
 </td>
 </tr>
	);
}

function TypeIcon(props: { type: CcpaRequestType }) {
	// Inline SVG per brand rules (no emoji). 14x14 monochrome.
	const common = "h-3.5 w-3.5 text-zinc-500";
	switch (props.type) {
 case "know":
 return (
 <svg
 className={common}
 viewBox="0 0 20 20"
 fill="none"
 stroke="currentColor"
 strokeWidth="1.8"
 aria-hidden="true"
 >
 <circle cx="10" cy="10" r="7.5" />
 <path d="M10 6v.01M9 10h1v4h1" strokeLinecap="round" />
 </svg>
 );
 case "delete":
 return (
 <svg
 className={common}
 viewBox="0 0 20 20"
 fill="none"
 stroke="currentColor"
 strokeWidth="1.8"
 aria-hidden="true"
 >
 <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10" strokeLinecap="round" />
 </svg>
 );
 case "opt_out_sale":
 return (
 <svg
 className={common}
 viewBox="0 0 20 20"
 fill="none"
 stroke="currentColor"
 strokeWidth="1.8"
 aria-hidden="true"
 >
 <circle cx="10" cy="10" r="7.5" />
 <path d="M5 5l10 10" strokeLinecap="round" />
 </svg>
 );
 case "limit_sensitive":
 return (
 <svg
 className={common}
 viewBox="0 0 20 20"
 fill="none"
 stroke="currentColor"
 strokeWidth="1.8"
 aria-hidden="true"
 >
 <rect x="4" y="8" width="12" height="9" rx="2" />
 <path d="M7 8V5.5a3 3 0 0 1 6 0V8" strokeLinecap="round" />
 </svg>
 );
	}
}

// ---------------------------------------------------------------------------
// Operator checklist
// ---------------------------------------------------------------------------

function OperatorChecklist() {
	return (
 <aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
 <p className="font-medium text-zinc-800">Operator checklist</p>
 <ul className="mt-2 flex list-inside list-disc flex-col gap-1">
 <li>
 Verify the subject's California residency before any status
 transition beyond received.
 </li>
 <li>
 Fulfilment windows are 45 days under CCPA 1798.130(a)(2); rows within
 10 days of the deadline surface in amber.
 </li>
 <li>
 Denials require an explicit reason — the audit trail persists the
 reason alongside the denied_at timestamp.
 </li>
 <li>
 The public do-not-sell payload is served from GET
 /api/v1/ccpa-dsr/do-not-sell and requires no authentication.
 </li>
 </ul>
 </aside>
	);
}
